import { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  FormLayout,
  TextField,
  Button,
  Select,
  Checkbox,
  Text,
  Banner,
  InlineStack,
  BlockStack,
  Box,
  Divider,
  Badge,
  Icon,
  Spinner
} from '@shopify/polaris';
import {
  CheckCircleIcon,
  RefreshIcon,
  LockIcon,
  AlertCircleIcon,
  ClipboardIcon
} from '@shopify/polaris-icons';
import { useAuthenticatedFetch } from '../App';

type ConnectionStatus = 'connected' | 'disconnected' | 'testing' | 'unknown';

interface CredentialsPayload {
  success: boolean;
  shop?: string;
  api_key?: string | null;
  tenant_id?: string | null;
  tenant_name?: string | null;
  region?: string | null;
  region_url?: string | null;
  last_sync_at?: string | null;
  api_version?: string | null;
  error?: string;
  message?: string;
}

interface TestConnectionPayload {
  ok: boolean;
  latency_ms?: number;
  shop?: string;
  tenant_id?: string;
  region?: string;
  error?: string;
  message?: string;
}

function formatLastSync(iso: string | null | undefined): string {
  if (!iso) return 'Never';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return iso;
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

export default function SettingsPage() {
  const authenticatedFetch = useAuthenticatedFetch();

  // Live tenant/credentials state — populated from the backend.
  const [apiKey, setApiKey] = useState('');
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantName, setTenantName] = useState<string | null>(null);
  const [region, setRegion] = useState<string | null>(null);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const [apiVersion, setApiVersion] = useState<string>('v2.0');

  // UI state
  const [credentialsLoading, setCredentialsLoading] = useState(true);
  const [credentialsError, setCredentialsError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('unknown');
  const [testResult, setTestResult] = useState<TestConnectionPayload | null>(null);

  // Other settings (placeholders until backend persistence lands)
  const [enableAnalytics, setEnableAnalytics] = useState(true);
  const [defaultPlacement, setDefaultPlacement] = useState('product-page');
  const [anonymizeIp, setAnonymizeIp] = useState(true);
  const [enableExport, setEnableExport] = useState(true);
  const [autoDelete, setAutoDelete] = useState(false);

  const loadCredentials = useCallback(async () => {
    setCredentialsLoading(true);
    setCredentialsError(null);
    try {
      const response = await authenticatedFetch('/api/commerce-studio/credentials');
      const data: CredentialsPayload = await response.json();

      if (response.ok && data.success) {
        setApiKey(data.api_key || '');
        setTenantId(data.tenant_id || null);
        setTenantName(data.tenant_name || null);
        setRegion(data.region || null);
        setLastSyncAt(data.last_sync_at || null);
        if (data.api_version) {
          setApiVersion(data.api_version.startsWith('v') ? data.api_version : `v${data.api_version}`);
        }
        setConnectionStatus(data.api_key ? 'connected' : 'disconnected');
      } else {
        setConnectionStatus('disconnected');
        setCredentialsError(
          data.message || data.error || `Unable to load credentials (HTTP ${response.status})`
        );
      }
    } catch (err) {
      setConnectionStatus('disconnected');
      const message = err instanceof Error ? err.message : 'Network error';
      setCredentialsError(message);
    } finally {
      setCredentialsLoading(false);
    }
  }, [authenticatedFetch]);

  useEffect(() => {
    loadCredentials();
  }, [loadCredentials]);

  const handleCopy = async () => {
    if (!apiKey) return;
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy api_key:', err);
    }
  };

  const handleSave = () => {
    // Settings persistence for analytics/privacy options is wired separately;
    // the api_key field is read-only here. TODO: editable api_key override
    // for support cases — leave to follow-up PR.
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const testConnection = async () => {
    setTesting(true);
    setConnectionStatus('testing');
    setTestResult(null);
    try {
      const response = await authenticatedFetch('/api/commerce-studio/test-connection');
      const data: TestConnectionPayload = await response.json();
      setTestResult(data);
      setConnectionStatus(response.ok && data.ok ? 'connected' : 'disconnected');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Network error';
      setConnectionStatus('disconnected');
      setTestResult({ ok: false, error: 'network_error', message });
    } finally {
      setTesting(false);
    }
  };

  const placementOptions = [
    { label: 'Product Page', value: 'product-page' },
    { label: 'Collection Page', value: 'collection-page' },
    { label: 'Cart Page', value: 'cart-page' },
    { label: 'Custom', value: 'custom' }
  ];

  const statusBadgeTone =
    connectionStatus === 'connected'
      ? 'success'
      : connectionStatus === 'testing'
        ? 'info'
        : 'critical';

  const statusLabel =
    connectionStatus === 'connected'
      ? 'Connected'
      : connectionStatus === 'testing'
        ? 'Testing…'
        : connectionStatus === 'unknown'
          ? 'Unknown'
          : 'Disconnected';

  return (
    <Page
      title="Settings"
      subtitle="Configure your Commerce Studio integration"
      backAction={{ content: 'Dashboard', url: '/' }}
      primaryAction={{
        content: 'Save Settings',
        onAction: handleSave,
        loading: false
      }}
    >
      <Layout>
        {saved && (
          <Layout.Section>
            <Banner
              title="Settings saved successfully!"
              tone="success"
              onDismiss={() => setSaved(false)}
            />
          </Layout.Section>
        )}

        {credentialsError && (
          <Layout.Section>
            <Banner
              title="Couldn't load Commerce Studio credentials"
              tone="critical"
              onDismiss={() => setCredentialsError(null)}
            >
              <p>{credentialsError}</p>
            </Banner>
          </Layout.Section>
        )}

        {testResult && (
          <Layout.Section>
            <Banner
              title={
                testResult.ok
                  ? `Connection OK${typeof testResult.latency_ms === 'number' ? ` (${testResult.latency_ms}ms)` : ''}`
                  : `Connection failed: ${testResult.error || 'unknown_error'}`
              }
              tone={testResult.ok ? 'success' : 'critical'}
              onDismiss={() => setTestResult(null)}
            >
              {!testResult.ok && testResult.message && <p>{testResult.message}</p>}
              {testResult.ok && testResult.tenant_id && (
                <p>Tenant: {testResult.tenant_id} ({testResult.region || 'unknown region'})</p>
              )}
            </Banner>
          </Layout.Section>
        )}

        {/* API Configuration */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <BlockStack gap="100">
                  <Text variant="headingMd" as="h2">Commerce Studio Configuration</Text>
                  <Text variant="bodySm" tone="subdued">
                    {tenantName ? `Tenant: ${tenantName}` : 'Configure your connection to Commerce Studio'}
                  </Text>
                </BlockStack>
                <Badge tone={statusBadgeTone}>
                  <InlineStack gap="100" blockAlign="center">
                    <Icon source={connectionStatus === 'connected' ? CheckCircleIcon : AlertCircleIcon} />
                    {statusLabel}
                  </InlineStack>
                </Badge>
              </InlineStack>

              <Divider />

              {credentialsLoading ? (
                <Box padding="400">
                  <InlineStack gap="200" blockAlign="center">
                    <Spinner accessibilityLabel="Loading credentials" size="small" />
                    <Text as="span" variant="bodySm" tone="subdued">Resolving credentials from Commerce Studio…</Text>
                  </InlineStack>
                </Box>
              ) : (
                <FormLayout>
                  <TextField
                    label="Commerce Studio API Key"
                    value={apiKey}
                    onChange={() => { /* read-only by design — paste-override deferred to follow-up */ }}
                    placeholder="cs_live_xxxxxxxxxxxxx"
                    helpText={
                      apiKey
                        ? `Auto-resolved from your Shopify shop. Tenant ID: ${tenantId || 'unknown'}`
                        : 'Could not auto-resolve. Re-open the app from your Shopify admin.'
                    }
                    type="password"
                    autoComplete="off"
                    readOnly
                    disabled
                    prefix={<Icon source={LockIcon} tone="subdued" />}
                    connectedRight={
                      <Button
                        icon={ClipboardIcon}
                        onClick={handleCopy}
                        disabled={!apiKey}
                        accessibilityLabel="Copy API key to clipboard"
                      >
                        {copied ? 'Copied!' : 'Copy'}
                      </Button>
                    }
                  />

                  <Select
                    label="Default Widget Placement"
                    options={placementOptions}
                    value={defaultPlacement}
                    onChange={setDefaultPlacement}
                    helpText="Where should widgets appear by default?"
                  />

                  <Checkbox
                    label="Enable Analytics Tracking"
                    checked={enableAnalytics}
                    onChange={setEnableAnalytics}
                    helpText="Allow Commerce Studio to track widget interactions for analytics"
                  />
                </FormLayout>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Privacy Settings */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <BlockStack gap="100">
                <Text variant="headingMd" as="h2">GDPR & Privacy</Text>
                <Text variant="bodySm" tone="subdued">
                  Configure data privacy controls for your customers
                </Text>
              </BlockStack>

              <Divider />

              <BlockStack gap="300">
                <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                  <InlineStack align="space-between" blockAlign="center">
                    <BlockStack gap="050">
                      <Text variant="bodyMd" fontWeight="semibold">Anonymize IP addresses</Text>
                      <Text variant="bodySm" tone="subdued">
                        Automatically anonymize customer IP addresses in analytics
                      </Text>
                    </BlockStack>
                    <Checkbox
                      label=""
                      labelHidden
                      checked={anonymizeIp}
                      onChange={setAnonymizeIp}
                    />
                  </InlineStack>
                </Box>

                <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                  <InlineStack align="space-between" blockAlign="center">
                    <BlockStack gap="050">
                      <Text variant="bodyMd" fontWeight="semibold">Enable data export for customers</Text>
                      <Text variant="bodySm" tone="subdued">
                        Allow customers to request their data
                      </Text>
                    </BlockStack>
                    <Checkbox
                      label=""
                      labelHidden
                      checked={enableExport}
                      onChange={setEnableExport}
                    />
                  </InlineStack>
                </Box>

                <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                  <InlineStack align="space-between" blockAlign="center">
                    <BlockStack gap="050">
                      <Text variant="bodyMd" fontWeight="semibold">Auto-delete data after 90 days</Text>
                      <Text variant="bodySm" tone="subdued">
                        Automatically remove customer interaction data
                      </Text>
                    </BlockStack>
                    <Checkbox
                      label=""
                      labelHidden
                      checked={autoDelete}
                      onChange={setAutoDelete}
                    />
                  </InlineStack>
                </Box>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Integration Status */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">Integration Status</Text>

              <Divider />

              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="span" variant="bodyMd">Connection Status</Text>
                  <Badge tone={statusBadgeTone}>{statusLabel}</Badge>
                </InlineStack>

                <InlineStack align="space-between" blockAlign="center">
                  <Text as="span" variant="bodyMd">Tenant</Text>
                  <Text as="span" variant="bodyMd" tone="subdued">{tenantName || tenantId || 'Unknown'}</Text>
                </InlineStack>

                <InlineStack align="space-between" blockAlign="center">
                  <Text as="span" variant="bodyMd">Region</Text>
                  <Badge tone={region === 'EU' ? 'info' : undefined}>{region || 'Unknown'}</Badge>
                </InlineStack>

                <InlineStack align="space-between" blockAlign="center">
                  <Text as="span" variant="bodyMd">Last Sync</Text>
                  <Text as="span" variant="bodyMd" tone="subdued">{formatLastSync(lastSyncAt)}</Text>
                </InlineStack>

                <InlineStack align="space-between" blockAlign="center">
                  <Text variant="bodyMd">API Version</Text>
                  <Badge>{apiVersion}</Badge>
                </InlineStack>
              </BlockStack>

              <Divider />

              <InlineStack gap="200">
                <Button
                  onClick={testConnection}
                  loading={testing}
                  icon={RefreshIcon}
                >
                  Test Connection
                </Button>
                <Button variant="plain" tone="critical">
                  Disconnect Integration
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Danger Zone */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <BlockStack gap="100">
                <Text variant="headingMd" as="h2" tone="critical">Danger Zone</Text>
                <Text variant="bodySm" tone="subdued">
                  Irreversible actions that affect your integration
                </Text>
              </BlockStack>

              <Divider />

              <InlineStack align="space-between" blockAlign="center">
                <BlockStack gap="050">
                  <Text variant="bodyMd" fontWeight="semibold">Reset all settings</Text>
                  <Text variant="bodySm" tone="subdued">
                    This will reset all Commerce Studio settings to defaults
                  </Text>
                </BlockStack>
                <Button tone="critical">Reset Settings</Button>
              </InlineStack>

              <InlineStack align="space-between" blockAlign="center">
                <BlockStack gap="050">
                  <Text variant="bodyMd" fontWeight="semibold">Delete all analytics data</Text>
                  <Text variant="bodySm" tone="subdued">
                    Permanently delete all collected analytics data
                  </Text>
                </BlockStack>
                <Button tone="critical">Delete Data</Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
